import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import NavBar from "@/components/NavBar";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import TodayCard from "@/components/admin/TodayCard";
import BookingEditDialog from "@/components/admin/BookingEditDialog";
import InquiryEditDialog from "@/components/admin/InquiryEditDialog";
import FollowUpDialog from "@/components/admin/FollowUpDialog";
import RegEditDialog from "@/components/admin/RegEditDialog";
import GalleryManager from "@/components/admin/GalleryManager";
import ContentTab from "@/components/admin/ContentTab";
import FinancialsTab from "@/components/admin/FinancialsTab";
import { Stat, TabBtn } from "@/components/admin/shared/UIPrimitives";
import { EmailTestPanel, GCalBanner } from "@/components/admin/AdminBanners";
import RegistrationsTable from "@/components/admin/tabs/RegistrationsTab";
import BookingsTable from "@/components/admin/tabs/BookingsTab";
import InquiriesTable from "@/components/admin/tabs/InquiriesTab";
import ClassesTable, { ClassDialog, RosterDialog } from "@/components/admin/tabs/ClassesTab";
import CreditsTab from "@/components/admin/tabs/CreditsTab";
import EmailTemplatesTab from "@/components/admin/tabs/EmailTemplatesTab";
import InvoicesTab from "@/components/admin/tabs/InvoicesTab";

// Suppress unused-import lint for TodayCard (still referenced elsewhere or planned)
void TodayCard;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("registrations");
  const [searchParams, setSearchParams] = useSearchParams();

  // Verify session via cookie-based /auth/me; redirect to login if not authed
  const { data: me, isError: meError } = useQuery({
    queryKey: ["me"],
    queryFn: async () => (await api.get("/auth/me")).data,
    retry: false,
  });

  useEffect(() => {
    if (meError) navigate("/admin/login", { replace: true });
  }, [meError, navigate]);

  // Toast on Google OAuth round-trip
  useEffect(() => {
    if (searchParams.get("gcal_connected")) {
      toast.success(`Google Calendar connected (${searchParams.get("gcal_connected")})`);
      setSearchParams({});
    }
    if (searchParams.get("gcal_error")) {
      toast.error("Could not connect Google Calendar — please try again.");
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const { data: classes = [], refetch: refetchClasses } = useQuery({
    queryKey: ["adminClasses"],
    queryFn: async () => (await api.get("/admin/classes")).data,
  });
  const { data: regs = [], refetch: refetchRegs } = useQuery({
    queryKey: ["adminRegs"],
    queryFn: async () => (await api.get("/admin/registrations?include_archived=true")).data,
  });
  const { data: bookings = [], refetch: refetchBookings } = useQuery({
    queryKey: ["adminBookings"],
    queryFn: async () => (await api.get("/admin/bookings")).data,
  });
  const { data: serviceRequests = [], refetch: refetchInquiries } = useQuery({
    queryKey: ["adminServiceRequests"],
    queryFn: async () => (await api.get("/admin/service-requests")).data,
  });
  const { data: gcal, refetch: refetchGcal } = useQuery({
    queryKey: ["gcalStatus"],
    queryFn: async () => (await api.get("/google/oauth/status")).data,
    retry: false,
  });

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    navigate("/admin/login", { replace: true });
  };

  const [editing, setEditing] = useState(null);
  const [editingReg, setEditingReg] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [followUpReg, setFollowUpReg] = useState(null);
  const [rosterCls, setRosterCls] = useState(null);

  const refreshAll = () => {
    refetchClasses();
    refetchRegs();
    refetchBookings();
    refetchInquiries();
    refetchGcal();
  };

  const openInquiries = serviceRequests.filter((r) => !r.archived && r.status !== "closed").length;
  const openBookings = bookings.filter((b) => b.status === "requested").length;

  return (
    <>
      <NavBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-gg-brown font-semibold">
              Admin
            </p>
            <h1 className="font-heading text-4xl text-[var(--gg-text)] mt-1">
              {me ? `Welcome, ${me.name}` : "Dashboard"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                const pid = window.prompt(
                  "Paste a Stripe payment ID to reconcile (pi_…, ch_…, or cs_…)\n\nUse this when a parent paid via Stripe but the registration still shows pending. The system will find the registration(s) by session metadata and mark them all paid."
                );
                if (!pid) return;
                try {
                  const { data } = await api.post("/admin/stripe/reconcile", { payment_id: pid.trim() });
                  toast.success(`Reconciled ${data.marked_paid} registration${data.marked_paid > 1 ? "s" : ""}`);
                  refreshAll();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Reconcile failed");
                }
              }}
              className="btn-pill btn-outline"
              type="button"
              data-testid="admin-stripe-reconcile-btn"
              title="Reconcile a Stripe payment that didn't auto-mark the registration"
            >
              Reconcile Stripe
            </button>
            <button
              onClick={refreshAll}
              className="btn-pill btn-outline"
              type="button"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              className="btn-pill btn-outline"
              data-testid={ADMIN.logoutBtn}
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </header>

        <GCalBanner gcal={gcal} refetch={refetchGcal} />
        <EmailTestPanel />

        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          <Stat label="Active classes" value={classes.filter((c) => c.status === "open").length} />
          <Stat label="Total registrations" value={regs.length} />
          <Stat
            label="ClassWallet pending"
            value={regs.filter((r) => r.classwallet_status === "pending").length}
            accent="warning"
          />
          <Stat
            label="Expired uploads"
            value={regs.filter((r) => r.classwallet_expired).length}
            accent="error"
          />
        </div>

        <div className="flex gap-2 border-b border-gg-border mb-6 flex-wrap">
          <TabBtn active={tab === "classes"} onClick={() => setTab("classes")} testId={ADMIN.tabClasses}>
            Classes &amp; Rosters
          </TabBtn>
          <TabBtn active={tab === "registrations"} onClick={() => setTab("registrations")} testId={ADMIN.tabRegistrations}>
            Registrations
          </TabBtn>
          <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} testId="admin-tab-bookings">
            1-on-1 Bookings
            {openBookings > 0 && (
              <span className="ml-2 inline-block min-w-[20px] text-center text-[10px] bg-[#D9933B] text-white rounded-full px-1.5 py-0.5">
                {openBookings}
              </span>
            )}
          </TabBtn>
          <TabBtn active={tab === "inquiries"} onClick={() => setTab("inquiries")} testId="admin-tab-inquiries">
            Inquiries
            {openInquiries > 0 && (
              <span className="ml-2 inline-block min-w-[20px] text-center text-[10px] bg-[#D9933B] text-white rounded-full px-1.5 py-0.5">
                {openInquiries}
              </span>
            )}
          </TabBtn>
          <TabBtn active={tab === "credits"} onClick={() => setTab("credits")} testId="admin-tab-credits">
            Credits
          </TabBtn>
          <TabBtn active={tab === "invoices"} onClick={() => setTab("invoices")} testId="admin-tab-invoices">
            Invoices
          </TabBtn>
          <TabBtn active={tab === "content"} onClick={() => setTab("content")} testId="admin-tab-content">
            Content
          </TabBtn>
          <TabBtn active={tab === "gallery"} onClick={() => setTab("gallery")} testId="admin-tab-gallery">
            Gallery
          </TabBtn>
          <TabBtn active={tab === "financials"} onClick={() => setTab("financials")} testId="admin-tab-financials">
            Financials
          </TabBtn>
          <TabBtn active={tab === "emails"} onClick={() => setTab("emails")} testId="admin-tab-emails">
            Emails
          </TabBtn>
        </div>

        {tab === "registrations" && (
          <RegistrationsTable regs={regs} refetch={refetchRegs} onEdit={setEditingReg} onFollowUp={setFollowUpReg} />
        )}
        {tab === "bookings" && (
          <BookingsTable bookings={bookings} refetch={refetchBookings} gcalConnected={gcal?.connected} onEdit={setEditingBooking} />
        )}
        {tab === "inquiries" && (
          <InquiriesTable requests={serviceRequests} refetch={refetchInquiries} onEdit={setEditingInquiry} />
        )}
        {tab === "credits" && <CreditsTab />}
        {tab === "invoices" && <InvoicesTab regs={regs} refetch={refetchRegs} />}
        {tab === "content" && <ContentTab />}
        {tab === "gallery" && <GalleryManager />}
        {tab === "financials" && <FinancialsTab />}
        {tab === "emails" && <EmailTemplatesTab />}
        {tab === "classes" && (
          <ClassesTable
            classes={classes}
            onEdit={(c) => setEditing(c)}
            onRoster={(c) => setRosterCls(c)}
            onNew={() =>
              setEditing({
                title: "",
                class_date: "",
                start_time: "",
                end_time: "",
                location: "Graceful Garden Homestead, Chino Valley, AZ",
                event_address: "",
                what_to_bring: "",
                price_student: 55,
                price_parent: 12,
                capacity_total: 20,
                description: "",
                curriculum_box: "",
                esa_alignment: "",
                admin_notes: "",
                materials_checklist: [],
                status: "open",
                allow_parent_addon: true,
                allow_siblings: true,
                allow_chaperones: true,
                direct_pay_only: false,
              })
            }
            refetch={refetchClasses}
          />
        )}

        {editing && (
          <ClassDialog
            cls={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              refetchClasses();
              qc.invalidateQueries({ queryKey: ["classes"] });
            }}
          />
        )}
        {editingReg && (
          <RegEditDialog
            reg={editingReg}
            onClose={() => setEditingReg(null)}
            onSaved={() => {
              setEditingReg(null);
              refetchRegs();
              refetchClasses();
            }}
          />
        )}
        {rosterCls && (
          <RosterDialog
            cls={rosterCls}
            onClose={() => setRosterCls(null)}
            onEditReg={(r) => { setRosterCls(null); setEditingReg(r); }}
          />
        )}
        {editingBooking && (
          <BookingEditDialog
            booking={editingBooking}
            onClose={() => setEditingBooking(null)}
            onSaved={() => { setEditingBooking(null); refetchBookings(); }}
          />
        )}
        {editingInquiry && (
          <InquiryEditDialog
            inquiry={editingInquiry}
            onClose={() => setEditingInquiry(null)}
            onSaved={() => { setEditingInquiry(null); refetchInquiries(); }}
          />
        )}
        {followUpReg && (
          <FollowUpDialog
            reg={followUpReg}
            onClose={() => setFollowUpReg(null)}
          />
        )}
      </main>
    </>
  );
}
